import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";

const CANONICAL = "https://www.moncommerce68.fr";

/**
 * Per-route SEO head manager. Injects title / description / canonical / OG /
 * Twitter tags + optional JSON-LD. Googlebot executes React since 2019 and
 * will pick these up. Also serves as a signal for LLM crawlers (GPTBot,
 * ClaudeBot, PerplexityBot) which are increasingly important for discovery.
 *
 * Usage:
 *   <SeoHelmet
 *     title="Plombier à Mulhouse — 12 adresses"
 *     description="Liste vérifiée..."
 *     path="/annuaire/mulhouse/plombier"
 *     jsonLd={[schema1, schema2]}
 *   />
 */
export default function SeoHelmet({ title, description, path = "/", jsonLd = [], noindex = false, ogImage }) {
  const canonical = CANONICAL + path;
  // Memoize the JSON-LD serialization — avoids re-stringifying identical objects on every render
  const serializedJsonLd = useMemo(
    () => jsonLd.filter(Boolean).map((obj) => JSON.stringify(obj)),
    [jsonLd]
  );
  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={noindex ? "noindex,follow" : "index,follow,max-image-preview:large"} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="fr" href={canonical} />
      <link rel="alternate" hrefLang="en" href={`${canonical}?lang=en`} />
      <link rel="alternate" hrefLang="de" href={`${canonical}?lang=de`} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <meta property="og:url" content={canonical} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Mon Commerce 68" />
      <meta property="og:locale" content="fr_FR" />
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {serializedJsonLd.map((payload, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {payload}
        </script>
      ))}
    </Helmet>
  );
}
